/**
 * 業務フロー（`getFlow` / `listPjFlows`）の TanStack Query ラッパー。
 *
 * 画面が `rpcClient` を直接叩かないための層である。Query キー（`query-keys.ts`）と入力の対応を
 * ここだけで決める。画面側で `useQuery` を直接書くと、同じ procedure に別のキーが付いた瞬間に
 * キャッシュが分裂する。
 *
 * `getFlow` は業務フロー 1 本（`flowId`）とステージの図（`stageId`）の 2 通りに引ける。出力は
 * `view` で分かれるので、フックも 2 本に分け、それぞれが自分の形だけを返す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 本・ステージの図・案件が変えた業務フローの一覧を TanStack Query のフックとして画面へ供給する。
 * @graph-connects rpcClient [calls] specGraph.getFlow
 * @graph-connects rpcClient [calls] specGraph.listPjFlows
 */
import type {
  FlowView,
  GetFlowOutput,
  ListPjFlowsOutput,
  StageView,
  SupportFlowCardView,
} from '@cortex/domain-spec-console';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';

/** 案件から見る面。変更前はマスター、変更後は未確定の変更を重ねた姿である。 @graph-connects none */
export type FlowPjSideName = 'before' | 'after';

/** 業務フローを引くときの案件の指定。 @graph-connects none */
export interface FlowPj {
  /** 案件番号（number）。 */
  number: number;
  /** 見る面。 */
  side: FlowPjSideName;
}

/** ステージの図 1 枚。 @graph-connects none */
export interface StageDiagram {
  /** ステージ */
  stage: StageView;
  /** 主フロー。まだ書かれていなければ null */
  mainFlow: FlowView | null;
  /** 支援フローの札 */
  supportFlows: SupportFlowCardView[];
}

/**
 * 出力が業務フロー 1 本であることを確かめて取り出す。
 *
 * `flowId` で引いた出力は必ず `view: 'flow'` である。違えば読取の不具合なので、握らずに投げて
 * 失敗として画面に出す。
 *
 * @param output 読取の出力
 * @returns 業務フロー 1 本
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business getFlow の出力から業務フロー 1 本を取り出し、形が違えば失敗にする。
 * @graph-connects none
 */
export function flowOfOutput(output: GetFlowOutput): FlowView {
  if (output.view !== 'flow') throw new Error('getFlow returned a stage view for a flowId');
  return output.flow;
}

/**
 * 出力がステージの図であることを確かめて取り出す。
 *
 * @param output 読取の出力
 * @returns ステージの図
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business getFlow の出力からステージの図を取り出し、形が違えば失敗にする。
 * @graph-connects none
 */
export function stageOfOutput(output: GetFlowOutput): StageDiagram {
  if (output.view !== 'stage') throw new Error('getFlow returned a flow view for a stageId');
  return { stage: output.stage, mainFlow: output.mainFlow, supportFlows: output.supportFlows };
}

/**
 * 業務フロー 1 本。
 *
 * 案件を渡すと、その面から見た姿が返る。渡さなければマスターである。**同じ業務フローを面違いで
 * 2 回引いて左右に並べる**ので、Query キーにも案件番号と面を載せる。
 *
 * **投げない選択を呼び出し側に渡す。** 案件が新設した業務フローには変更前が無いので、引けば必ず
 * 未存在で返る。呼び出し側が先に知れたときは引かない。
 *
 * @param flowId 業務フローの id
 * @param pj 重ねる案件と面。マスターを読むなら省く
 * @param enabled 取得してよいか。省けば取得する
 * @returns 業務フロー 1 本
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 本を、マスターまたは指定した案件の面から見た姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.getFlow
 */
export function useFlow(flowId: number, pj?: FlowPj, enabled = true): UseQueryResult<FlowView> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.getFlow('flow', flowId, locale, pj?.number, pj?.side),
    queryFn: async () =>
      flowOfOutput(
        await rpcClient.specGraph.getFlow({
          flowId,
          locale,
          ...(pj === undefined ? {} : { pj }),
        }),
      ),
    enabled: enabled && flowId > 0,
  });
}

/**
 * ステージの図 1 枚（主フロー ＋ 支援フローの札）。
 *
 * @param stageId ステージの id
 * @param pj 重ねる案件と面。マスターを読むなら省く
 * @param enabled 取得してよいか。省けば取得する
 * @returns ステージの図
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの図（主フローと支援フローの札）を、マスターまたは指定した案件の面から見た姿として取得する。
 * @graph-connects rpcClient [calls] specGraph.getFlow
 */
export function useStage(
  stageId: number,
  pj?: FlowPj,
  enabled = true,
): UseQueryResult<StageDiagram> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.getFlow('stage', stageId, locale, pj?.number, pj?.side),
    queryFn: async () =>
      stageOfOutput(
        await rpcClient.specGraph.getFlow({
          stageId,
          locale,
          ...(pj === undefined ? {} : { pj }),
        }),
      ),
    enabled: enabled && stageId > 0,
  });
}

/**
 * 案件が変えた業務フローの一覧。変更の種類（追加・更新・削除）つき。
 *
 * @param pjNumber 案件番号
 * @returns 案件が変えた業務フローの一覧
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が変えた業務フローの一覧を、変更の種類つきで取得する。
 * @graph-connects rpcClient [calls] specGraph.listPjFlows
 */
export function usePjFlows(pjNumber: number): UseQueryResult<ListPjFlowsOutput> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.listPjFlows(pjNumber, locale),
    queryFn: () => rpcClient.specGraph.listPjFlows({ pjNumber, locale }),
    enabled: pjNumber > 0,
  });
}
